import { Injectable, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ProductService,
  MetadataService,
  LocationConfigService,
  GungFlowService,
  Product,
  PimConcept,
  AvailabilityService,
  AuthService
} from 'gung-standard';
import { ColmecPimConceptsService } from './colmec-pim-concepts.service';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  ConfigService,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs';
import { ColmecConceptProductsTableComponent } from '../components/colmec-concept-products-table/colmec-concept-products-table.component';

export class ItemTypeFilterAr extends SimpleConfigBaseFilter<Product> {
  constructor(
    protected filterTitle: string,
    protected field: string,
    protected notFoundString: string,
    protected metadataService?: MetadataService, // Necessary to get Metadata
    protected tableName?: string, // Necessary to get Metadata
    protected valueName?: string // Necessary to get Metadata
  ) {
    super();
  }

  getName(): string {
    return this.filterTitle;
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra?.ar?.hasOwnProperty(this.field)) {
      return [this.notFoundString];
    }
    return ['' + item.extra.ar[this.field]];
  }

  getOptionName(key: string): string {
    if (!this.tableName || !this.valueName) {
      return key;
    }
    return this.metadataService.getMetadataValue(this.tableName, this.valueName, key);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ColmecConceptProductsListConfigService implements ConfigService<Product> {
  @Input()
  public concept: PimConcept;

  constructor(
    protected productService: ProductService,
    protected metadataService: MetadataService,
    protected locationConfigService: LocationConfigService,
    protected route: ActivatedRoute,
    protected gungFlowService: GungFlowService,
    protected colmecPimConceptsService: ColmecPimConceptsService,
    protected availabilityService: AvailabilityService,
    protected authService: AuthService
  ) {}

  getSortOptions(): ListSortOption<Product>[] {
    // TODO: implement
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getItemId(item: Product) {
    return item.id;
  }

  getSearchTerms(item: Product) {
    return [item.id, item.name];
  }

  getItems(): Observable<Product[]> {
    return this.colmecPimConceptsService.getProductsByConceptById(this.concept.id).pipe(
      catchError((err, caught) => {
        console.error('ERROR GETTING PRODUCTS: ' + err.error.message);
        return of([] as Product[]);
      }),
      mergeMap(products =>
        forkJoin([
          of(products),
          this.authService.getCurrentUser().pipe(
            first(),
            map(user => user.managedMultistockIds[0])
          )
        ])
      ),
      switchMap(([products, multistock]) => {
        const ids = products.map(p => p.id);
        return forkJoin([
          this.productService.getProductsByIds(ids),
          this.availabilityService.getAvailabilities(ids, multistock, true)
        ]);
      }),
      mergeMap(([products, availabilities]) => {
        products.forEach(p => {
          p.extra.availability = availabilities.find(avail => avail.productId === p.id);
        });
        return of(products.filter(p => p.extra.availability && p.extra.availability.currentAvailability > 0));
      })
    );
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fas fa-th-list',
        getListItemComponent: () => ColmecConceptProductsTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    return [
      // new ItemTypeFilterAr('AR_VARUGRUPPKOD', 'varugruppkod', 'NO_BRAND_DEFINED', this.metadataService, 'vg', 'varugruppbeskr'),
      // new ItemTypeFilterAr('AR_Q_KLASSID', 'q_klassid', 'NO_STOMKLASS', this.metadataService, 'q_klass', 'q_klassdesc'),
    ];
  }
}
